import { AddEventListernerResult, ChatMessageData, CyclingQRCodeData, EventListenerMap, EventListeners, QRCodeData } from "@kalyzee/kast-web-sdk/web-components";
import { ChatroomStatus, Meeting, MeetingRoom, MeetingSession, MeetingSessionMedia } from "@kalyzee/kast-web-sdk/websocket";
import { PageMap, PageSlaveMode } from "../../components/navigation/PageContext";
import { AppBroadcastChannel, AppMessage, appMessage, AppMessageEventMap } from "../message";
import { generateRandomId } from "../utils";
import { MessagingBridgeManagerMaster } from "./master";
import { MessagingBridgeManagerSlave } from "./slave";
import { SessionMode } from "../../contexts/appStore/hooks";
import { ThemePreference } from "@kalyzee/kalyzee-ui";

const LOG = false;

export enum MessagingBridgeMessageAction {
  WELCOME = "welcome", // ONLY FOR BROADCAST CHANNEL
  AUTHORIZED = "authorized",
  UNAUTHORIZED = "unauthorized",
  PING = "ping",
  PONG = "pong",
  SET_CLIENT_DATA = "set_client_data",
  CLEAR_CLIENT_DATA = 'clear_client_data',

  GET_MEETING = "get_meeting",
  SET_MEETING = "set_meeting",

  GET_CHAT = 'get_chat',
  SET_CHAT = 'set_chat',

  SET_QR_CODE_DATA = 'set_qr_code_data',

  SET_THEME = 'set_theme',

  DISPLAY_CAROUSEL = 'display_carousel',
}

export interface MessagingBridgeClientDataMap {
  [SessionMode.MASTER]: {};
  [SessionMode.SLAVE]: {
    [PageSlaveMode.MEETING]: {
      layoutId?: number,
    }
  };
}

export interface MessagingBridgeClientData<M extends SessionMode, T extends PageMap[M]> {
  mode: M;
  page: T;
  data: T extends keyof MessagingBridgeClientDataMap[M] ? MessagingBridgeClientDataMap[M][T] : undefined;
}

/**
 * Package sent to a screen to represent the current state of multiple carousels
 * at once.
 */
export type ClientContainersIteration = { 
  containers: {
    id: string,
    sessions: MeetingSessionMedia[],
    totalSessions: number,
    page: number,
    pageCount: number,
    carouselId: string,
    groupId: string,
  }[],
};

export interface MessagingBridgeMessageContentMap {
  [MessagingBridgeMessageAction.WELCOME]: { masterId: string };
  [MessagingBridgeMessageAction.AUTHORIZED]: undefined;
  [MessagingBridgeMessageAction.UNAUTHORIZED]: { code: number };
  [MessagingBridgeMessageAction.PING]: { counter: number };
  [MessagingBridgeMessageAction.PONG]: { counter: number };
  [MessagingBridgeMessageAction.SET_CLIENT_DATA]: MessagingBridgeClientData<any,any>;
  [MessagingBridgeMessageAction.CLEAR_CLIENT_DATA]: {mode: SessionMode, page: PageMap[SessionMode] };

  [MessagingBridgeMessageAction.GET_MEETING]: undefined;
  [MessagingBridgeMessageAction.SET_MEETING]: { meeting?: Meeting; session?: MeetingSession<false> };

  [MessagingBridgeMessageAction.GET_CHAT]: { chatroomId: string };
  [MessagingBridgeMessageAction.SET_CHAT]: { chatroomId: string, state: ChatroomStatus, messages: ChatMessageData[]};

  [MessagingBridgeMessageAction.SET_QR_CODE_DATA]: { data: CyclingQRCodeData };

  [MessagingBridgeMessageAction.SET_THEME]: ThemePreference;

  [MessagingBridgeMessageAction.DISPLAY_CAROUSEL] : { data: ClientContainersIteration, delay: number },
}

export interface MessagingBridgeMessage<T extends MessagingBridgeMessageAction> {
  id: string;
  action: T;
  content: MessagingBridgeMessageContentMap[T];
}

export interface MessagingBridgeMessageExtended<T extends MessagingBridgeMessageAction> extends MessagingBridgeMessage<T> {
  fromId: string;
  from: SessionMode;
}

export interface MessagingBridgeBroadcastChannelMessage<T extends MessagingBridgeMessageAction> extends MessagingBridgeMessageExtended<T> {
  toId?: string;
}

export interface MessagingBridgeManagerOptions {
  enableBroadcast?: boolean;
  password?: string;
}

const defaultBridgeMessageManagerOptions = {
  enableBroadcast: false,
} as const;

export abstract class MessagingBridgeManager<T extends SessionMode, K extends string, M extends EventListenerMap<K>> {
  protected events: EventListeners<K, M>;

  public logsEnabled: boolean = LOG;

  readonly id: string;
  readonly mode: SessionMode;
  readonly broadcastEnabled: boolean;

  readonly appMessage?: AppMessage;
  readonly appBroadcastChannel?: AppBroadcastChannel;

  protected appListener: [keyof AppMessageEventMap, AppMessageEventMap[keyof AppMessageEventMap]][] = [];

  protected currentMeetingId?: string;

  protected password?: string;

  constructor(mode: T, options?: MessagingBridgeManagerOptions) {
    this.events = new EventListeners();
    this.id = generateRandomId(16);
    this.mode = mode;
    this.broadcastEnabled = options?.enableBroadcast ?? defaultBridgeMessageManagerOptions.enableBroadcast;
    this.appMessage = appMessage;
    if (this.broadcastEnabled) {
      this.appBroadcastChannel = new AppBroadcastChannel();
    }
    this.password = options?.password;
    this.log("CREATE BRIDGE : ", mode);
  }

  static isMasterManager(manager?: MessagingBridgeManager<any, any, any>): manager is MessagingBridgeManagerMaster {
    return manager && manager.mode === SessionMode.MASTER ? true : false;
  }
  static isSlaveManager(manager?: MessagingBridgeManager<any, any, any>): manager is MessagingBridgeManagerSlave {
    return manager && manager.mode === SessionMode.SLAVE ? true : false;
  }

  static isMessagingBridgeClientData<M extends SessionMode, T extends PageMap[M]>(
    content?: MessagingBridgeClientData<any, any>,
    mode?: M | undefined,
    page?: T | undefined
  ): content is MessagingBridgeClientData<M, T> {
    if (!content || !page) return false;
    return content.mode === mode && content.page === page;
  }

  static isMessageExtented<T extends MessagingBridgeMessageAction>(
    msg: MessagingBridgeMessageExtended<any>,
    action: T
  ): msg is MessagingBridgeMessageExtended<T> {
    if (!msg) return false;
    return msg.action === action;
  }

  protected buildBridgeMessage<T extends MessagingBridgeMessageAction>(action: T, content: MessagingBridgeMessageContentMap[T]): MessagingBridgeMessage<T> {
    return { id: generateRandomId(8), action, content };
  }

  public destroy() {
    this.appListener.forEach((listener) => {
      this.appMessage?.removeEventListener(...listener);
    });
    this.appBroadcastChannel?.close();
  }

  public log(...args: any[]) {
    if (!this.logsEnabled) return;
    const style = "color:green;font-weight:bold";
    const data = new Date();
    console.log(`%c[MESSAGING-BRIDGE -- ${this.mode.toUpperCase()} -- ${data.toLocaleTimeString()}:${data.getMilliseconds()}] `, style, ...args);
  }

  protected abstract post(msg: MessagingBridgeMessage<any>, toId?: string): void;
  protected abstract proccess(msg: MessagingBridgeMessage<any>, toId?: string): void;

  // ---------------------------- LISTENER -----------------------//
  addEventListener<T extends K>(event: T, listener: M[T]): AddEventListernerResult<M, T> {
    return this.events.addEventListener(event, listener);
  }

  removeEventListener<T extends K>(event: T, listener: M[T]) {
    return this.events.removeEventListener(event, listener);
  }
}
